import { Component, DestroyRef, computed, effect, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { createForm, group } from '@sonofabit/ng-core';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { AbstractControl, FormArray, FormControl, Validators } from '@angular/forms';
import { PendingChanges } from '@guards/pending-changes.guard';
import { BehaviorSubject, firstValueFrom, Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import {
  BuildingService,
  ControlSystemDto,
  SensorBlockDetailDto,
  SensorBlockMergeDto,
  SensorBlockService,
  SensorDetailDto
} from '@artemis-software/wr-api';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { tryWithLoading } from '@/utils/async-utils';
import { v4 as uuid } from 'uuid';
import { ConfirmDialogComponent } from '@components/dialog/confirm-dialog/confirm-dialog.component';
import { DeleteDialogComponent } from '@components/dialog/delete-dialog/delete-dialog.component';
import { queryParam, routeParam } from '@/utils/signals/router';
import { SensorUnitMetadataService } from '@services/sensor-unit-metadata.service';
import { reisenbauerGuidValidator } from '@/utils/validators';
import { SensorBlockChartPageComponent } from '@pages/sensor-block-chart-page/sensor-block-chart-page.component';
import SensorIntegrationTypeEnum = SensorDetailDto.SensorIntegrationTypeEnum;
import SensorPurposeEnum = SensorDetailDto.PurposeEnum;
import SensorMetricKindEnum = SensorDetailDto.SensorMetricKindEnum;

@Component({
  standalone: false,
  selector: 'wr-sensor-detail-page',
  templateUrl: './sensor-block-detail-page.component.html',
  styleUrl: './sensor-block-detail-page.component.scss'
})
export class SensorBlockDetailPageComponent implements PendingChanges {

  private readonly nbDialogService = inject(NbDialogService);
  private readonly sensorBlockService = inject(SensorBlockService);
  private readonly buildingService = inject(BuildingService);
  private readonly nbToastrService = inject(NbToastrService);
  private readonly router = inject(Router);
  private readonly sensorUnitMetadataService = inject(SensorUnitMetadataService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly formSubscriptions = new Subject<void>();

  readonly monitoringPurpose = SensorPurposeEnum.Monitoring;
  readonly cumulativeMetricKind = SensorMetricKindEnum.Cumulative;

  loading$ = new BehaviorSubject(false);
  buildingId: string | null = null;
  isEditing = false;

  form?: ReturnType<typeof this.createFormFromDetail>;
  readonly selectedIndex = signal<number>(0);

  firstSubmit = false;

  readonly isNew = signal(false);
  readonly canEdit = signal(false);
  readonly canDelete = signal(false);

  readonly idParam = routeParam('id');
  readonly buildingIdParam = queryParam('buildingId');
  readonly chartView = viewChild(SensorBlockChartPageComponent);

  readonly currentControlSystemId = signal<string | null>(null);

  readonly buildingControlSystemIds = signal<string[]>([]);

  private syncedSourceSnapshot = new Map<string, { functionBlockId: string, fbAddress: string, connectorId: string }>();

  readonly controlSystems = asyncComputed<ControlSystemDto[]>(() => {
    if (!this.canEdit() && !this.isNew()) {
      return [];
    }
    return this.sensorBlockService.findAllControlSystems();
  }, []);

  readonly controlSystemOptions = computed(() => {
    const list = this.controlSystems();
    const buildingControlSystemIds = this.buildingControlSystemIds();
    const filtered = buildingControlSystemIds.length > 0
      ? list.filter((cs) => buildingControlSystemIds.includes(cs.id))
      : list;
    const current = this.currentControlSystemId();
    if (current && !filtered.some((cs) => cs.id === current)) {
      return [...filtered, { id: current, name: current }];
    }
    return filtered;
  });

  constructor() {
    effect(() => {
      const id = this.idParam();
      const buildingId = this.buildingIdParam();
      this.buildingId = buildingId;

      if (id) {
        if (id === 'new' && buildingId) {
          this.isEditing = true;
          this.isNew.set(true);
          this.form = this.createFormFromDetail(null, buildingId);
          this.buildingService.findById(buildingId).subscribe((building) => {
            this.buildingControlSystemIds.set(building.controlSystemIds ?? []);
          });
        } else {
          this.sensorBlockService.findById(id).subscribe((sensorBlock) => {
            this.canEdit.set(sensorBlock.permissions.canEdit);
            this.canDelete.set(sensorBlock.permissions.canEdit);
            this.buildingControlSystemIds.set(sensorBlock.building.controlSystemIds ?? []);
            this.form = this.createFormFromDetail(sensorBlock);
            this.form?.disable();
          });
        }
      } else {
        this.navigateBack();
      }
    }, { allowSignalWrites: true });
  }

  createFormFromDetail(detail: SensorBlockDetailDto | null, buildingId?: string) {
    this.currentControlSystemId.set(detail?.controlSystemId ?? null);
    this.syncedSourceSnapshot = new Map(
      (detail?.sensors ?? [])
        .map((sensor) => [
          sensor.id,
          {
            functionBlockId: sensor.functionBlockId,
            fbAddress: sensor.fbAddress,
            connectorId: sensor.connectorId ?? '',
          },
        ]),
    );
    const form = createForm(({ field, array }) => {
      return {
        id: field({ value: detail?.id }),
        name: field({ value: detail?.name, validators: [Validators.required] }),
        description: field({ value: detail?.description }),
        controlSystemId: field({
          value: detail?.controlSystemId ?? '',
          validators: [reisenbauerGuidValidator()],
        }),
        color: field({ value: detail?.color ?? '#000000', validators: [Validators.required] }),
        buildingId: field({ value: buildingId }),
        sensors: array((detail?.sensors ?? []).map(sensor => {
          return group({
            id: field({ value: sensor.id }),
            localId: field({ value: uuid() }),
            name: field({ value: sensor.name, validators: [Validators.required] }),
            description: field({ value: sensor.description }),
            color: field({ value: sensor.color, validators: [Validators.required] }),
            fbAddress: field({ value: sensor.fbAddress, validators: [Validators.pattern(/^\d{0,4}$/)] }),
            connectorId: field({ value: sensor.connectorId ?? '', validators: [Validators.pattern(/^\d{0,3}$/)] }),
            functionBlockId: field({
              value: sensor.functionBlockId,
              validators: [reisenbauerGuidValidator()],
            }),
            sensorUnitLabel: field({ value: { sensorUnit: sensor.sensorUnit, label: sensor.label } }),
            displayUnit: field({ value: sensor.displayUnit }),
            sensorMetricKind: field({ value: sensor.sensorMetricKind, validators: [Validators.required] }),
            purpose: field({ value: sensor.purpose, validators: [Validators.required] }),
            anchorValue: field({ value: sensor.anchorValue, validators: [Validators.min(0)] }),
            anchorTimestamp: field({ value: sensor.anchorTimestamp ? new Date(sensor.anchorTimestamp) : null }),
            isActive: field({ value: sensor.isActive }),
            isVisible: field({ value: sensor.isVisible }),
          });
        }
        )),
      };
    });
    this.formSubscriptions.next();
    (form.get('sensors') as FormArray).controls.forEach((sensorGroup) => {
      this.clearDisplayUnitOnDimensionChange(sensorGroup);
      this.clearAnchorOnMetricKindChange(sensorGroup);
    });
    return form;
  }

  private clearAnchorOnMetricKindChange(sensorGroup: AbstractControl): void {
    sensorGroup.get('sensorMetricKind')?.valueChanges
      .pipe(takeUntil(this.formSubscriptions), takeUntilDestroyed(this.destroyRef))
      .subscribe((metricKind) => {
        if (metricKind === this.cumulativeMetricKind) {
          return;
        }
        sensorGroup.get('anchorValue')?.setValue(null);
        sensorGroup.get('anchorTimestamp')?.setValue(null);
      });
  }

  private clearDisplayUnitOnDimensionChange(sensorGroup: AbstractControl): void {
    const displayUnit = sensorGroup.get('displayUnit');
    sensorGroup.get('sensorUnitLabel')?.valueChanges
      .pipe(takeUntil(this.formSubscriptions), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        const current = displayUnit?.value;
        const compatibleUnits = value?.sensorUnit
          ? this.sensorUnitMetadataService.unitsOfSameDimension(value.sensorUnit)
          : [];
        if (current && !compatibleUnits.includes(current)) {
          displayUnit?.setValue(undefined);
        }
      });
  }

  get sensors(): FormArray {
    return this.form?.get('sensors') as FormArray;
  }

  navigateBack(): void {
    if (this.buildingId) {
      this.router.navigate(['/dashboard/building-new', this.buildingId, 'sensors']);
    } else {
      window.history.back();
    }
  }

  onEdit(): void {
    this.isEditing = true;
    this.form?.enable();
  }

  onDeleteSensorBlock(): void {
    this.nbDialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Sensor Block löschen',
        message: 'Bist du sicher, dass du den Sensor Block löschen willst?',
      },
    }).onClose.subscribe({
      next: (result) => {
        const sensorId = this.form?.value.id;
        if (result && sensorId) {
          this.sensorBlockService.deleteById(sensorId).subscribe(() => {
            this.nbToastrService.success('Sensor Block wurde erfolgreich gelöscht', 'Sensor Block');
            this.navigateBack();
          });
        }
      }, error: () => {
        this.nbToastrService.danger('Beim Löschen des Sensor Blocks ist ein Fehler aufgetreten', 'Sensor Block');
      },
    });
  }

  onDeleteSensor(index: number): void {
    this.nbDialogService.open(DeleteDialogComponent, {
      context: {
        title: 'Sensor löschen',
        message: 'Bist du sicher, dass du den Sensor löschen willst?',
      },
    }).onClose.subscribe({
      next: (result) => {
        if (result) {
          this.sensors.removeAt(index);
          this.nbToastrService.success('Sensor wurde erfolgreich gelöscht', 'Sensor');
        }
      }, error: () => {
        this.nbToastrService.danger('Beim Löschen des Sensors ist ein Fehler aufgetreten', 'Sensor');
      },
    });
  }

  async submit(): Promise<void> {
    this.firstSubmit = true;
    this.form?.markAsTouched();

    if (!this.form?.valid) {
      this.nbToastrService.danger('Sensor Block kann nicht gespeichert werden, es gibt noch Fehler.', 'Sensor Block');
      return;
    }

    const sourceChanged = this.hasSyncedSourceChange();
    if (sourceChanged) {
      const confirmed = await firstValueFrom(this.nbDialogService.open(ConfirmDialogComponent, {
        context: {
          header: 'Messdaten neu aufbauen?',
          message: 'Die Datenquelle (Messstation, Funktionsblock-ID, FB-Adresse oder Datenpunkt) von mindestens einem synchronisierten Sensor wurde geändert. ' +
            'Beim Speichern werden dessen bisherige Messdaten gelöscht und von Grund auf neu synchronisiert.',
          status: 'warning',
          submitText: 'Speichern',
        },
      }).onClose);
      if (confirmed !== true) {
        return;
      }
    }

    await tryWithLoading(this.loading$, async () => {
      const res = this.form?.value;
      const sensors = res?.sensors?.map((sensor) => {
        const sensorUnit = sensor.sensorUnitLabel?.sensorUnit;
        const compatibleUnits = sensorUnit ? this.sensorUnitMetadataService.unitsOfSameDimension(sensorUnit) : [];
        const displayUnit =
          sensor.displayUnit && compatibleUnits.includes(sensor.displayUnit) ? sensor.displayUnit : undefined;
        // Der Anker wird serverseitig nur auf dieses Signal hin geloescht - fehlende Felder
        // bedeuten dort "unveraendert", damit ein Teil-Merge keine Anker wegwirft. Ein halb
        // geleertes Paar weist der Server als Widerspruch ab, deshalb gehen beide Felder leer raus.
        const clearAnchor = typeof sensor.anchorValue !== 'number' || !sensor.anchorTimestamp;
        return {
          ...sensor,
          sensorUnit,
          displayUnit,
          labelReference: { value: sensor.sensorUnitLabel?.label?.id },
          anchorValue: clearAnchor ? undefined : sensor.anchorValue,
          anchorTimestamp: clearAnchor ? undefined : sensor.anchorTimestamp,
          clearAnchor,
        };
      });
      const sensorMergeDto = {
        ...this.form?.value,
        sensors: sensors,
      } as SensorBlockMergeDto;
      const wasNew = this.isNew();
      const saved = await firstValueFrom(this.sensorBlockService.merge(sensorMergeDto));
      this.form?.get('id')?.setValue(saved.id);
      if (sourceChanged && saved.id) {
        try {
          await firstValueFrom(this.sensorBlockService.triggerSensorBlockSynchronization(saved.id));
          this.nbToastrService.info('Messdaten werden im Hintergrund neu synchronisiert.', 'Sensor Block');
        } catch (err: unknown) {
          if ((err as { status?: number })?.status !== 503) {
            this.nbToastrService.warning(
              'Gespeichert, die Neusynchronisierung der Messdaten konnte aber nicht gestartet werden.',
              'Sensor Block',
            );
          }
        }
      }
      await this.reloadForm();
      this.chartView()?.refresh();
      this.nbToastrService.success('Sensor Block wurde erfolgreich gespeichert', 'Sensor Block');

      if (wasNew && this.buildingId) {
        this.isNew.set(false);
        this.navigateBack();
      }
    });
  }

  private hasSyncedSourceChange(): boolean {
    const value = this.form?.value;
    if (!value) {
      return false;
    }
    const controlSystemChanged = (value.controlSystemId ?? '') !== (this.currentControlSystemId() ?? '');
    return (value.sensors ?? []).some((sensor) => {
      const snapshot = sensor.id ? this.syncedSourceSnapshot.get(sensor.id) : undefined;
      if (!snapshot) {
        return false;
      }
      return controlSystemChanged
        || (sensor.functionBlockId ?? '') !== snapshot.functionBlockId
        || (sensor.fbAddress ?? '') !== snapshot.fbAddress
        || ((sensor.connectorId ?? '') !== '' && (sensor.connectorId ?? '') !== snapshot.connectorId);
    });
  }

  async reloadForm(): Promise<void> {
    await tryWithLoading(this.loading$, async () => {
      const id = this.form?.value.id;
      if (id) {
        const sensorBlock = await firstValueFrom(this.sensorBlockService.findById(id));
        this.form = this.createFormFromDetail(sensorBlock);
      }
    });
    this.isEditing = false;
    this.form?.disable();
  }

  hasPendingChanges(): boolean | Promise<boolean> {
    return this.isEditing;
  }

  cancel(): void {
    this.navigateBack();
  }

  createNewSensor() {
    const sensorGroup = group({
      id: new FormControl(undefined),
      localId: new FormControl(uuid()),
      name: new FormControl('', [Validators.required]),
      description: new FormControl(''),
      color: new FormControl('#000000', [Validators.required]),
      fbAddress: new FormControl('', [Validators.pattern(/^\d{0,4}$/)]),
      connectorId: new FormControl('', [Validators.pattern(/^\d{0,3}$/)]),
      sensorMetricKind: new FormControl(undefined, [Validators.required]),
      purpose: new FormControl(undefined, [Validators.required]),
      sensorIntegrationType: new FormControl(SensorIntegrationTypeEnum.ReisenbauerSensor, [Validators.required]), // TODO: remove type
      functionBlockId: new FormControl('', [reisenbauerGuidValidator()]),
      sensorUnitLabel: new FormControl({ sensorUnit: undefined, label: undefined }),
      displayUnit: new FormControl(undefined),
      anchorValue: new FormControl(undefined),
      anchorTimestamp: new FormControl(undefined),
      isActive: new FormControl(false),
      isVisible: new FormControl(false),
    });
    this.clearDisplayUnitOnDimensionChange(sensorGroup);
    this.clearAnchorOnMetricKindChange(sensorGroup);
    this.sensors.push(sensorGroup);
    this.selectedIndex.set(this.sensors.length - 1);
  }
}
